import React, { Component } from "react";
import gql from "graphql-tag";
import { graphql } from "react-apollo";
import { withRouter } from "react-router-dom";
import * as compose from "lodash.flowright";
import * as Cookies from "es-cookie";
import { Row, Col } from "react-bootstrap";
import TabSection from "./TabSection";
import { FeePageShimmer } from "../FeeStructure/FeeShimmer";

const GET_DOCUMENT_LOGS = gql`
  query getDocumentLogs(
    $institute_id: Int!
    $branch_id: Int
    $class_id: Int
    $section_id: Int
  ) {
    getDocumentLogs(
      institute_id: $institute_id
      branch_id: $branch_id
      class_id: $class_id
      section_id: $section_id
    ) {
      student_id
      submitted_ids
      submitted_count
      submitted_names
      pending_ids
      pending_count
      pending_names
      returned_ids
      returned_count
      returned_names
      collected_comments
      pending_comments
      return_comments
    }
  }
`;

// ── Field normalisers (backend returns array | JSON string | CSV) ──────────────
const parseNames = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map((s) => String(s).trim());
  const s = String(raw).trim();
  if (!s) return [];
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) return parsed.filter(Boolean).map((x) => String(x).trim());
  } catch (e) {
    /* fall through to CSV */
  }
  return s.split(",").map((x) => x.trim()).filter(Boolean);
};

// Each comment field is parallel to its names array ("||"-separated or JSON array).
const parseCommentMap = (raw, docNames = []) => {
  if (raw === null || raw === undefined) return {};
  if (Array.isArray(raw)) {
    const map = {};
    docNames.forEach((name, i) => { if (raw[i]) map[name] = String(raw[i]); });
    return map;
  }
  if (typeof raw === "object") return raw;
  const s = String(raw).trim();
  if (!s) return {};
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) {
      const map = {};
      docNames.forEach((name, i) => { if (parsed[i]) map[name] = String(parsed[i]); });
      return map;
    }
    if (parsed && typeof parsed === "object") return parsed;
  } catch (e) {
    /* fall through to "||" split */
  }
  const parts = s.split("||").map((x) => x.trim());
  const map = {};
  docNames.forEach((name, i) => { if (parts[i]) map[name] = parts[i]; });
  return map;
};

// Status presentation — one entry per document state in the log.
const STATUS_META = {
  submitted: { label: "Submitted", color: "#16a34a", bg: "#f0fdf4", icon: "fa-check-circle" },
  pending: { label: "Pending", color: "#b45309", bg: "#fef3c7", icon: "fa-hourglass-half" },
  returned: { label: "Returned", color: "#1d4ed8", bg: "#eff6ff", icon: "fa-reply" },
};

const toInt = (v) => {
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
};

// edu_student_id is preferred; fall back to student_user_id, then student_id.
const resolveStudentId = (student) => {
  if (!student) return null;
  if (student.edu_student_id !== null && student.edu_student_id !== undefined) {
    return parseInt(student.edu_student_id);
  }
  if (student.student_user_id !== null && student.student_user_id !== undefined) {
    return parseInt(student.student_user_id);
  }
  if (student.student_id !== null && student.student_id !== undefined) {
    return parseInt(student.student_id);
  }
  return null;
};

class DocumentsTab extends Component {
  // Locate this student's record within the (branch/class/section-filtered) log set.
  studentRecord = () => {
    const logs = this.props.documentLogs?.getDocumentLogs || [];
    const resolved = resolveStudentId(this.props.student);
    if (resolved === null) return null;
    const sid = String(resolved);
    return logs.find((l) => String(l.student_id) === sid) || null;
  };

  // Flatten the three name lists into one ordered list of { name, status, comment }.
  buildDocuments = (record) => {
    if (!record) return [];
    const submitted = parseNames(record.submitted_names);
    const pending = parseNames(record.pending_names);
    const returned = parseNames(record.returned_names);

    const collectedMap = parseCommentMap(record.collected_comments, submitted);
    const pendingMap = parseCommentMap(record.pending_comments, pending);
    const returnMap = parseCommentMap(record.return_comments, returned);

    return [
      ...submitted.map((name) => ({ name, status: "submitted", comment: collectedMap[name] || "" })),
      ...returned.map((name) => ({ name, status: "returned", comment: returnMap[name] || "" })),
      ...pending.map((name) => ({ name, status: "pending", comment: pendingMap[name] || "" })),
    ];
  };

  renderCounts = (record) => {
    const items = [
      { key: "submitted", count: record.submitted_count ?? parseNames(record.submitted_names).length },
      { key: "pending", count: record.pending_count ?? parseNames(record.pending_names).length },
      { key: "returned", count: record.returned_count ?? parseNames(record.returned_names).length },
    ];
    return (
      <Row className="mb-4">
        {items.map(({ key, count }) => {
          const meta = STATUS_META[key];
          return (
            <Col md={3} key={key} className="mb-3 mb-md-0">
              <div
                className="d-flex align-items-center h-100"
                style={{
                  gap: "10px",
                  padding: "10px 16px",
                  borderRadius: "12px",
                  background: meta.bg,
                  border: `1px solid ${meta.color}22`,
                }}
              >
                <i className={`fas ${meta.icon}`} style={{ color: meta.color, fontSize: "16px" }} />
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>{meta.label}</span>
                <strong style={{ fontSize: "16px", color: meta.color }}>{count}</strong>
              </div>
            </Col>
          );
        })}
      </Row>
    );
  };

  renderCard = (doc, idx) => {
    const meta = STATUS_META[doc.status];
    return (
      <Col md={6} key={`${doc.status}-${doc.name}-${idx}`} className="mb-3">
        <div
          className="d-flex align-items-center justify-content-between p-3 h-100"
          style={{
            borderRadius: "14px",
            background: "#ffffff",
            border: "1px solid #f1f2f5",
            boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
          }}
        >
          <div className="d-flex align-items-center" style={{ gap: "14px", minWidth: 0 }}>
            <div
              className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: "42px", height: "42px", borderRadius: "10px", background: meta.bg, color: meta.color }}
            >
              <i className="fas fa-file-alt" style={{ fontSize: "16px" }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <span className="d-block font-weight-bold text-dark text-truncate" style={{ fontSize: "14px" }}>
                {doc.name}
              </span>
              {doc.comment ? (
                <span className="d-block text-muted text-truncate" style={{ fontSize: "12px" }} title={doc.comment}>
                  {doc.comment}
                </span>
              ) : (
                <span className="d-block text-muted" style={{ fontSize: "12px" }}>
                  {meta.label}
                </span>
              )}
            </div>
          </div>
          <span
            className="flex-shrink-0 ml-2"
            style={{
              background: meta.bg,
              color: meta.color,
              padding: "4px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {meta.label}
          </span>
        </div>
      </Col>
    );
  };

  renderBody = () => {
    const { student } = this.props;
    const query = this.props.documentLogs || {};

    if (query.loading) {
      return <FeePageShimmer />;
    }

    if (query.error) {
      return (
        <div style={{ fontSize: "14px", color: "#dc2626" }}>
          <i className="fas fa-exclamation-circle mr-2" />
          Failed to load documents.
        </div>
      );
    }

    const record = this.studentRecord();
    const documents = this.buildDocuments(record);

    if (!record || documents.length === 0) {
      return (
        <div className="text-muted" style={{ fontSize: "14px" }}>
          No document records found for {student?.student_name || "this student"}.
        </div>
      );
    }

    return (
      <div>
        {this.renderCounts(record)}
        <Row>{documents.map(this.renderCard)}</Row>
      </div>
    );
  };

  render() {
    return (
      <TabSection
        icon="fa-id-card"
        title="Documents"
        description="Submitted, pending and returned admission documents."
      >
        {this.renderBody()}
      </TabSection>
    );
  }
}

export default withRouter(
  compose(
    graphql(GET_DOCUMENT_LOGS, {
      name: "documentLogs",
      options: (props) => {
        const s = props.student || {};
        return {
          variables: {
            institute_id: toInt(Cookies.get("institutionid") || Cookies.get("institute_id")) || 0,
            branch_id: toInt(s.branch ?? s.branch_id),
            class_id: toInt(s.class ?? s.class_id),
            section_id: toInt(s.section ?? s.section_id),
          },
          fetchPolicy: "network-only",
        };
      },
    })
  )(DocumentsTab)
);
